/**
 * One board's socket, as a component holds it: the elements, the status, and
 * everybody else's cursors and live strokes.
 *
 * Shared by the board page and the board inside a meeting, so the two cannot
 * disagree about what a cursor is or when it goes. A guest passes the meeting
 * token, which only ever reaches the socket's own `?token=`.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { openBoard } from '../../lib/board/client';
import { BoardPeers, type PeerView } from '../../lib/board/peers';
import type { BoardClient, BoardElement, BoardOp, BoardStatus } from '../../lib/board/types';

/** How often quiet cursors are looked for. Well under `PEER_TTL_MS`, so one
 *  goes within a second of its time being up. */
const EXPIRE_EVERY_MS = 1_000;

const NOBODY: PeerView = { cursors: [], live: [], here: [] };

export function useBoard(id: string, guestToken?: string) {
  const [elements, setElements] = useState<BoardElement[]>([]);
  const [status, setStatus] = useState<BoardStatus>('connecting');
  const [problem, setProblem] = useState('');
  const [others, setOthers] = useState<PeerView>(NOBODY);
  /** Counts the times the board moved room while open (close 4007). */
  const [moved, setMoved] = useState(0);
  const client = useRef<BoardClient | null>(null);

  useEffect(() => {
    const socket = openBoard();
    client.current = socket;
    const peers = new BoardPeers();
    const show = () => setOthers(peers.view());

    const offs = [
      socket.on('sync', (els) => setElements([...els])),
      socket.on('status', (next, message) => {
        setStatus(next);
        if (message) setProblem(message);
      }),
      socket.on('presence', (parties, you) => {
        peers.presence(parties, you);
        show();
      }),
      socket.on('cursor', (by, x, y) => {
        peers.cursor(by, x, y, Date.now());
        show();
      }),
      socket.on('live', (by, el) => {
        peers.live(by, el, Date.now());
        show();
      }),
      socket.on('op', (remote) => {
        peers.op(remote);
        show();
      }),
      socket.on('moved', () => setMoved((n) => n + 1))
    ];
    const sweep = setInterval(() => {
      if (peers.expire(Date.now())) show();
    }, EXPIRE_EVERY_MS);

    void socket.connect(id, guestToken);
    return () => {
      clearInterval(sweep);
      for (const off of offs) off();
      socket.close();
      client.current = null;
      setOthers(NOBODY);
    };
  }, [id, guestToken]);

  const apply = useCallback((op: BoardOp) => client.current?.apply(op), []);
  const cursor = useCallback((x: number, y: number) => client.current?.cursor(x, y), []);
  const live = useCallback((el: BoardElement | null) => client.current?.live(el), []);

  return { elements, status, problem, others, moved, apply, cursor, live };
}
